/**
 * In-Canvas runner for the client-side measurement calculation.
 *
 * Mounted inside the workspace <Canvas>. When the user clicks "Calculate" (or
 * picks a grid spacing) with the `frontend-measurement-calculation` flag on, the
 * trigger fires (see trigger.ts); this component gathers luminaires + IES grids,
 * illuminated layer geometry (cached while the object view is mounted) and norm
 * targets, runs the worker, and populates `version.measurementData`.
 *
 * Inputs are read fresh from the store at run time (not from a render-synced
 * ref), so a spacing change + immediate recompute uses the new value. Logs to
 * the console for verification.
 */
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { useFrame } from '@react-three/fiber';

import { useAppDispatch } from '../../../hooks';
import { httpGET } from '../../../services/http';
import {
  setFrontendMeasurementCalculating,
  setFrontendMeasurementData,
  setMeasurementSource,
} from '../../../store/reducers/workspace.reducer';
import { getNorms } from '../../../store/selectors/user.selector';
import { selectLampWireframeMap } from '../../../store/selectors/workspace.selector';
import { store } from '../../../store/store';
import type { RootState } from '../../../store/store';
import type { PresignedUrlResponse } from '../../../types/file';
import type { Lamp } from '../../../types/lamp';
import type { IesCandelaGrid } from '../../../types/lampWireframes';
import type { Light } from '../../../types/light';
import { fetchLdcAndBuildWireframeWithGrid } from '../../../util/ldc';
import {
  buildCalcInput,
  buildCalcLayers,
  buildCalcLuminaires,
  fieldsFromTargets,
  normToExpectedValues,
  resolveCalcRotationRadians,
} from '../../../utils/measurementCalculation/adapter';
import type { LayerSource, LuminaireSource } from '../../../utils/measurementCalculation/adapter';
import { shouldUseFrontendMeasurementSource } from '../../../utils/measurementCalculation/measurementSource';
import {
  onFrontendMeasurementCalculationRequest,
  requestFrontendMeasurementCalculation,
} from '../../../utils/measurementCalculation/trigger';
import type { MeasurementField, SurfaceGeometry } from '../../../utils/measurementCalculation/types';
import { MeasurementCalcSuperseded, measurementWorkerManager } from '../../../workers/measurementWorkerManager';
import { decompressMeasurementData } from './measurementUtils';
import { useIlluminatedGeometry } from './useIlluminatedGeometry';

/** taman-ml `calculationConfig.maintenanceFactor` (metadata.json) — kept in sync for 1:1 parity. */
const ML_MAINTENANCE_FACTOR = 0.9;

/** Debounce for auto-recalculation after a luminaire edit — coalesces drag steps. */
const AUTO_RECALC_DEBOUNCE_MS = 400;

/**
 * Signature of every luminaire input that changes the measurement result
 * (position, mounting height, catalogue product/variant, aim rotation). A change
 * to this string means a recompute is warranted; unrelated store updates (e.g.
 * selection) leave it untouched so they don't trigger a needless recalculation.
 */
const lampMeasurementSignature = (lamps: readonly Lamp[]): string =>
  lamps
    .map(
      (l) =>
        `${l.uuid}:${l.position[0]},${l.position[1]},${l.position[2]},` +
        `${l.distanceToGround},${l.rotationRadians ?? ''},${l.productId ?? ''},${l.variantId ?? ''}`,
    )
    .join('|');

export const FrontendMeasurementRunner = (): null => {
  const dispatch = useAppDispatch();
  const extractGeometry = useIlluminatedGeometry();
  const modelLayers = useSelector((s: RootState) => s.workspace.modelLayers);
  const useFrontendMeasurement = useSelector((s: RootState) =>
    shouldUseFrontendMeasurementSource(
      s.featureFlags.userFeatureFlags,
      s.workspace.project?.versions?.find((v) => v.id === s.workspace.project?.activeVersionId),
    ),
  );
  const measurementSource = useSelector((s: RootState) => s.workspace.measurementSource);
  const activeVersionId = useSelector((s: RootState) => s.workspace.project?.activeVersionId);
  const lampSignature = useSelector((s: RootState) => lampMeasurementSignature(s.workspace.lamps));
  const maxApexAngleDegrees = useSelector((s: RootState) => s.workspace.maxApexAngleDegrees);
  const measurementInputSignature = `${lampSignature}|apex:${maxApexAngleDegrees}`;
  // Whether usable photometry (IES file or catalogue variant) is present for the
  // active version's lights. A backend calculation finishing populates `iesFileId`
  // asynchronously, so a recompute requested the instant the "calculation finished"
  // notification fires can run BEFORE that data lands and bail with "no usable
  // luminaires". This signature changing afterwards lets us retry automatically
  // instead of leaving the measurement failed until a page refresh.
  const luminaireReadiness = useSelector((s: RootState) => {
    const project = s.workspace.project;
    const av = project?.versions?.find((v) => v.id === project?.activeVersionId);
    const lightsWithPhotometry = av?.lights?.filter((l) => l.iesFileId != null || l.variantId != null).length ?? 0;
    const lampsWithPhotometry = s.workspace.lamps.filter((l) => l.variantId != null || l.iesFileId != null).length;
    return `${lightsWithPhotometry}:${lampsWithPhotometry}`;
  });

  const geometryCache = useRef<Map<string, SurfaceGeometry>>(new Map());
  const geometryRequestSentRef = useRef(false);
  const iesGridCache = useRef<Map<number, IesCandelaGrid>>(new Map());
  // Catalogue variant photometry (market-matched versions): grids cached by
  // variantId, product payloads cached by productId to resolve LDC URLs.
  const variantGridCache = useRef<Map<number, IesCandelaGrid | null>>(new Map());
  const productCache = useRef<Map<number, { variants?: Array<{ id: string | number; ldcFileUrl?: string }> } | null>>(
    new Map(),
  );
  const extractRef = useRef(extractGeometry);
  extractRef.current = extractGeometry;

  // Concurrency guard: only ONE worker run at a time. A new request supersedes
  // the in-flight one (cancel its callback + recycle the worker so the stale
  // synchronous computation is abandoned). `runId` tracks the latest run so a
  // superseded run never flips the "calculating" flag off underneath its
  // successor or dispatches a stale result.
  const currentHandleRef = useRef<{ cancel: () => void } | null>(null);
  const runIdRef = useRef(0);
  // Set when a run bailed purely because no luminaire had usable photometry yet
  // (IES still loading). Cleared once a run gets usable luminaires. Drives the
  // automatic retry when `luminaireReadiness` changes.
  const bailedNoLuminairesRef = useRef(false);

  const refreshGeometryCache = (): boolean => {
    const extracted = extractRef.current();
    if (!extracted.size) return false;
    geometryCache.current = extracted;
    return true;
  };

  const maybeRequestMeasurement = (): void => {
    const state = store.getState();
    const project = state.workspace.project;
    const activeVersion = project?.versions?.find((v) => v.id === project?.activeVersionId);
    const useFrontend = shouldUseFrontendMeasurementSource(state.featureFlags.userFeatureFlags, activeVersion);
    if (useFrontend && !state.workspace.frontendMeasurementData && !state.workspace.frontendMeasurementCalculating) {
      requestFrontendMeasurementCalculation();
    }
  };

  useEffect(() => {
    geometryRequestSentRef.current = false;
    bailedNoLuminairesRef.current = false;
  }, [modelLayers, activeVersionId]);

  // Retry once photometry becomes available after a run bailed for lack of
  // usable luminaires (see `bailedNoLuminairesRef`). The ref is false on mount
  // and after any successful run, so this only fires to recover from a bail.
  useEffect(() => {
    if (!bailedNoLuminairesRef.current) return;
    bailedNoLuminairesRef.current = false;
    requestFrontendMeasurementCalculation();
  }, [luminaireReadiness]);

  // Meshes mount asynchronously after modelLayers hydrate — poll the live scene each frame
  // until geometry is extractable, then trigger a single measurement request.
  useFrame(() => {
    if (geometryRequestSentRef.current) return;
    if (!refreshGeometryCache()) return;
    geometryRequestSentRef.current = true;
    maybeRequestMeasurement();
  });

  // Also react when layer metadata changes after geometry is already in the scene.
  useEffect(() => {
    if (!refreshGeometryCache()) return;
    geometryRequestSentRef.current = true;
    maybeRequestMeasurement();
  }, [extractGeometry, modelLayers]);

  useEffect(() => {
    return onFrontendMeasurementCalculationRequest(() => {
      let geometry = extractRef.current();
      if (!geometry.size) geometry = geometryCache.current;
      if (!geometry.size) {
        console.warn('[frontend-calc] defer: no illuminated geometry yet');
        return;
      }

      console.info('[frontend-calc] request received by runner');
      // Supersede any in-flight run so we never pile multiple full calculations
      // onto the single synchronous worker (the observed "computing ×3" hang).
      // recycle() rejects the in-flight promise with MeasurementCalcSuperseded so
      // its awaiting run() unwinds cleanly (swallowed below).
      if (currentHandleRef.current) {
        currentHandleRef.current = null;
        measurementWorkerManager.recycle();
      }
      const myRunId = (runIdRef.current += 1);
      dispatch(setFrontendMeasurementCalculating(true));
      void run(myRunId).finally(() => {
        if (runIdRef.current === myRunId) dispatch(setFrontendMeasurementCalculating(false));
      });
    });

    async function run(runId: number): Promise<void> {
      const state = store.getState();
      console.info('[frontend-calc] run() — gridSpacingM =', state.workspace.measurementGridSpacing);
      const project = state.workspace.project;
      const activeVersion = project?.versions?.find((v) => v.id === project?.activeVersionId);
      if (!activeVersion) {
        console.warn('[frontend-calc] BAIL: no active version');
        return;
      }

      let geometry = extractRef.current();
      const geometrySource = geometry.size ? 'live-scene' : geometryCache.current.size ? 'cache' : 'none';
      if (!geometry.size) geometry = geometryCache.current;
      console.info('[frontend-calc] geometry source =', geometrySource, '| layers =', geometry.size);
      if (!geometry.size) {
        console.warn('[frontend-calc] BAIL: no illuminated geometry — waiting for models to load');
        return;
      }

      const wireframeMap = selectLampWireframeMap(state);
      const thirdPartyWireframes = state.workspace.lampWireframes.thirdPartyWireframes;
      // Workspace lamps carry live edits (positions, swapped products); version
      // lights may lag behind by up to one auto-save cycle. Prefer workspace
      // state for the catalogue variant assignment.
      const workspaceLamps = state.workspace.lamps;
      const lights = activeVersion.lights;
      const withIesFileId = lights.filter((l) => l.iesFileId != null).length;
      const withVariantId =
        workspaceLamps.filter((l) => l.variantId != null).length || lights.filter((l) => l.variantId != null).length;

      /** Resolve a catalogue variant's candela grid via its LDC file (cached). */
      const loadVariantGrid = async (productId: number, variantId: number): Promise<IesCandelaGrid | undefined> => {
        const cached = variantGridCache.current.get(variantId);
        if (cached !== undefined) return cached ?? undefined;
        let grid: IesCandelaGrid | null = null;
        try {
          let product = productCache.current.get(productId);
          if (product === undefined) {
            const resp = await httpGET(`/catalogue/products/${productId}`);
            product = (resp.data as { variants?: Array<{ id: string | number; ldcFileUrl?: string }> }) ?? null;
            productCache.current.set(productId, product);
          }
          const variant = product?.variants?.find((v) => v.id === variantId || String(v.id) === String(variantId));
          if (variant?.ldcFileUrl) {
            const { grid: parsed } = await fetchLdcAndBuildWireframeWithGrid(variant.ldcFileUrl);
            grid = parsed;
          }
        } catch (err) {
          console.warn('[frontend-calc] failed to load variant LDC', { productId, variantId }, err);
        }
        variantGridCache.current.set(variantId, grid);
        return grid ?? undefined;
      };

      // Calculation inputs: prefer live workspace lamps (they carry unsaved
      // position edits and product swaps); fall back to version lights when the
      // workspace hasn't hydrated yet.
      const lightsByUuid = new Map(lights.map((l) => [l.uuid, l]));
      const calcLights: Light[] =
        workspaceLamps.length > 0
          ? workspaceLamps.map((lamp) => {
              const versionLight = lightsByUuid.get(lamp.uuid);
              // The Lamp.tsx raycaster sets distanceToGround to -1 when no floor
              // mesh intersects (lamp at scene edge, mesh gap, transitioning views).
              // Always prefer the raycasted value when it's a valid positive height;
              // fall back to the API-persisted version-light value otherwise so the
              // calculation is never corrupted by a stale -1.
              const distToGround =
                lamp.distanceToGround != null && lamp.distanceToGround > 0
                  ? lamp.distanceToGround
                  : (versionLight?.distanceToGround ?? 0);
              return {
                ...(versionLight ?? { rotationRadians: undefined }),
                name: lamp.name,
                uuid: lamp.uuid,
                positionX: lamp.position[0],
                positionY: lamp.position[1],
                positionZ: lamp.position[2],
                distanceToGround: distToGround,
                iesFileId: lamp.iesFileId ?? versionLight?.iesFileId,
                productId: lamp.productId ?? versionLight?.productId,
                variantId: lamp.variantId ?? versionLight?.variantId,
                rotationRadians: resolveCalcRotationRadians(
                  lamp.uuid,
                  lamp.rotationRadians ?? versionLight?.rotationRadians,
                  wireframeMap,
                  thirdPartyWireframes,
                ),
              };
            })
          : lights.map((light) => ({
              ...light,
              rotationRadians: resolveCalcRotationRadians(
                light.uuid,
                light.rotationRadians,
                wireframeMap,
                thirdPartyWireframes,
              ),
            }));

      // Resolve each lamp's IES candela grid: from loaded wireframes, the
      // catalogue variant LDC (market-matched versions), the per-fetch cache,
      // or fetch+parse the IES file on demand (no dependency on the "Show IES
      // models" toggle).
      const luminaireSources: LuminaireSource[] = [];
      for (const light of calcLights) {
        let iesGrid = wireframeMap.get(light.uuid)?.wireframe?.iesGrid;
        if (!iesGrid && light.productId != null && light.variantId != null) {
          iesGrid = await loadVariantGrid(light.productId, light.variantId);
        }
        if (!iesGrid && light.iesFileId != null) {
          iesGrid = iesGridCache.current.get(light.iesFileId);
          if (!iesGrid) {
            try {
              const resp = await httpGET(`/files/${light.iesFileId}/presigned-url/download`);
              const url = (resp.data as PresignedUrlResponse).url;
              const { grid } = await fetchLdcAndBuildWireframeWithGrid(url);
              iesGrid = grid;
              iesGridCache.current.set(light.iesFileId, grid);
            } catch (err) {
              console.warn('[frontend-calc] failed to load IES for light', light.name, err);
            }
          }
        }
        luminaireSources.push({ light, iesGrid });
      }

      const luminaires = buildCalcLuminaires(luminaireSources);
      console.info(
        '[frontend-calc] lights =',
        lights.length,
        '| with iesFileId =',
        withIesFileId,
        '| with variantId =',
        withVariantId,
        '| usable (IES grid) =',
        luminaires.length,
      );
      if (!luminaires.length) {
        // Remember this so the readiness watcher retries once photometry lands
        // (the backend calc that produces the IES may still be propagating).
        bailedNoLuminairesRef.current = true;
        console.warn(
          '[frontend-calc] BAIL: no usable luminaires.' +
            (withIesFileId === 0 && withVariantId === 0
              ? ' Lights have no iesFileId — run a backend calculation first (ml produces the IES).'
              : ' IES/LDC files exist but could not be loaded/parsed.'),
        );
        return;
      }
      bailedNoLuminairesRef.current = false;

      const norms = getNorms(state);
      // ALL layers (ml computes every mesh, not just normed ones). Norm is optional.
      const layerSources: LayerSource[] = [];
      for (const layer of state.workspace.modelLayers) {
        const geo = geometry.get(layer.threeJsName);
        if (!geo) continue;
        const targets = layer.customTargets?.length ? layer.customTargets : undefined;
        const norm = targets ? undefined : norms.find((n) => n.name === layer.selectedNorm);
        layerSources.push({ uuid: layer.uuid, name: layer.originalName, geometry: geo, norm, targets });
      }
      const normedCount = layerSources.filter((s) => s.norm).length;
      console.info('[frontend-calc] layers with geometry =', layerSources.length, '| with norm =', normedCount);
      if (!layerSources.length) {
        console.warn('[frontend-calc] BAIL: no layers with geometry');
        return;
      }

      // Global field set: union of all norms' target functions — ml computes these for every layer.
      const fieldsFromNorms = fieldsFromTargets(
        layerSources.flatMap((s) => (s.targets?.length ? s.targets : s.norm ? normToExpectedValues(s.norm) : [])),
      );
      // When no layer has a norm/target the union is empty, which would make the engine mark
      // every photometric field NOT_COMPUTED (-1). Fall back to the fields the measurement view
      // can always display so E/Ev/Esc still render.
      const globalFields: MeasurementField[] = fieldsFromNorms.length ? fieldsFromNorms : ['E', 'Ev', 'Esc'];

      const gridSpacingM = state.workspace.measurementGridSpacing;
      // taman-ml uses calculationConfig.maintenanceFactor = 0.9 (from metadata.json),
      // not the workspace UI value — use the same so results match 1:1.
      // TODO: source this from the project's calc config once the frontend loads it.
      const input = buildCalcInput(luminaires, buildCalcLayers(layerSources, globalFields), {
        maintenanceFactor: ML_MAINTENANCE_FACTOR,
        gridSpacingM,
        maxApexAngleDegrees: state.workspace.maxApexAngleDegrees,
      });

      // A newer request arrived while this run was still loading photometry (before it had a
      // worker handle to cancel) — leave the worker to that run instead of computing twice.
      if (runIdRef.current !== runId) {
        console.info('[frontend-calc] superseded before computing');
        return;
      }
      console.info('[frontend-calc] computing', {
        gridSpacingM,
        luminaires: luminaires.length,
        layers: layerSources.length,
      });
      const startedAt = performance.now();
      const handle = measurementWorkerManager.calculate(input);
      try {
        currentHandleRef.current = handle;
        const measurementData = await handle.promise;
        if (currentHandleRef.current === handle) currentHandleRef.current = null;
        // A newer run superseded this one while it was computing — drop the result.
        if (runIdRef.current !== runId) return;
        dispatch(setFrontendMeasurementData(measurementData));
        dispatch(setMeasurementSource('frontend'));
        const perLayer = measurementData.map((m) => {
          const positions = decompressMeasurementData<number[][]>(m.positions);
          const meas = decompressMeasurementData<number[][]>(m.measurements);
          const ign = m.ignoredPoints ? decompressMeasurementData<number[]>(m.ignoredPoints) : positions.map(() => 0);
          // stats over non-ignored, computed values — same shape as the ml table
          const stat = (idx: number): { n: number; min: number; avg: number; max: number } | null => {
            let n = 0;
            let sum = 0;
            let mn = Infinity;
            let mx = -Infinity;
            for (let i = 0; i < meas.length; i += 1) {
              if (ign[i] === 1) continue;
              const v = meas[i]?.[idx];
              if (v === undefined || v === -1) continue;
              n += 1;
              sum += v;
              if (v < mn) mn = v;
              if (v > mx) mx = v;
            }
            return n ? { n, min: +mn.toFixed(2), avg: +(sum / n).toFixed(2), max: +mx.toFixed(2) } : null;
          };
          return {
            layer: m.name,
            points: positions.length,
            ignored: ign.filter((v) => v === 1).length,
            E: stat(1),
            Ev: stat(2),
            Esc: stat(3),
          };
        });
        const totalPoints = perLayer.reduce((sum, c) => sum + c.points, 0);
        console.info('[frontend-calc] done', {
          gridSpacingM,
          totalPoints,
          ms: Math.round(performance.now() - startedAt),
          perLayer,
        });
      } catch (error) {
        // Superseded runs reject on purpose when a fresher request recycles the
        // worker — that's expected, not a failure. Leave the handle alone: the
        // superseding request already replaced it (or recycled the worker).
        if (error instanceof MeasurementCalcSuperseded) return;
        // Genuine failure: clear the handle if it's still ours so a later request
        // doesn't see a stale handle and recycle the worker (losing its warm
        // caches) for no reason. If a newer run already replaced it, leave it be.
        if (currentHandleRef.current === handle) currentHandleRef.current = null;
        console.error('[frontend-calc] failed', error);
      }
    }
  }, [dispatch]);

  // Auto-recalculate when a luminaire input changes (position, height, catalogue
  // variant, rotation). Gated to when the user is actually previewing frontend
  // results, since luminaires are only movable in the editing view (the scene is
  // hidden in the measurement view): the worker recomputes in the background so a
  // fresh result is ready the moment they switch back. Debounced so dragging a
  // lamp across the scene coalesces into a single recompute when it settles.
  const autoRecalcActive = useFrontendMeasurement && measurementSource === 'frontend';
  const lampSignatureRef = useRef<string | null>(null);
  const autoRecalcTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Establish the baseline on first observation; never recompute on mount.
    if (lampSignatureRef.current === null) {
      lampSignatureRef.current = measurementInputSignature;
      return;
    }
    if (lampSignatureRef.current === measurementInputSignature) return;
    lampSignatureRef.current = measurementInputSignature;

    if (!autoRecalcActive) return;
    if (autoRecalcTimer.current) clearTimeout(autoRecalcTimer.current);
    autoRecalcTimer.current = setTimeout(() => {
      console.info('[frontend-calc] auto-recalc: luminaire inputs changed');
      requestFrontendMeasurementCalculation();
    }, AUTO_RECALC_DEBOUNCE_MS);
  }, [measurementInputSignature, autoRecalcActive]);

  useEffect(
    () => () => {
      if (autoRecalcTimer.current) clearTimeout(autoRecalcTimer.current);
    },
    [],
  );

  return null;
};
